import { useEffect, useRef, useState } from 'react';

/**
 * Message vocal court (micro du navigateur), utilisé par le signalement
 * « photo d'abord » des agents d'entretien : un tap pour parler, un tap pour
 * arrêter, arrêt automatique au bout de `maxSeconds`.
 *
 * Chrome/Android enregistrent en WebM/Opus ; Safari iOS ne sait faire que du MP4/AAC.
 */
export interface RecordedAudio {
    blob: Blob;
    url: string;
    /** Extension du fichier envoyé : le serveur la transmet telle quelle à la transcription. */
    extension: 'webm' | 'm4a';
}

export type VoiceRecorderError = 'denied' | 'failed';

const WEBM_MIME = 'audio/webm;codecs=opus';

export function pickAudioMimeType(): string {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(WEBM_MIME) ? WEBM_MIME : 'audio/mp4';
}

export function isVoiceRecordingSupported(): boolean {
    return typeof window !== 'undefined'
        && typeof MediaRecorder !== 'undefined'
        && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export function useVoiceRecorder(maxSeconds = 60) {
    const recorderRef = useRef<MediaRecorder | null>(null);
    const stopWaitersRef = useRef<Array<(audio: RecordedAudio | null) => void>>([]);
    const audioRef = useRef<RecordedAudio | null>(null);
    /** `clear()` pendant l'enregistrement : le message arrêté est jeté. */
    const discardRef = useRef(false);
    const [isRecording, setIsRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [audio, setAudio] = useState<RecordedAudio | null>(null);
    const [error, setError] = useState<VoiceRecorderError | null>(null);

    const replaceAudio = (next: RecordedAudio | null) => {
        if (audioRef.current) URL.revokeObjectURL(audioRef.current.url);
        audioRef.current = next;
        setAudio(next);
    };

    useEffect(() => {
        if (!isRecording) return undefined;
        const startedAt = Date.now();
        const timer = window.setInterval(() => {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            setSeconds(elapsed);
            if (elapsed >= maxSeconds) recorderRef.current?.stop();
        }, 250);
        return () => window.clearInterval(timer);
    }, [isRecording, maxSeconds]);

    useEffect(() => () => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        if (audioRef.current) URL.revokeObjectURL(audioRef.current.url);
    }, []);

    const start = async () => {
        setError(null);
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            setError('denied');
            return;
        }
        try {
            const mimeType = pickAudioMimeType();
            const recorder = new MediaRecorder(stream, { mimeType });
            const chunks: Blob[] = [];
            recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
                const type = recorder.mimeType || mimeType;
                const blob = new Blob(chunks, { type: type.split(';')[0] });
                const keep = blob.size > 0 && !discardRef.current;
                discardRef.current = false;
                const next: RecordedAudio | null = keep
                    ? { blob, url: URL.createObjectURL(blob), extension: type.includes('webm') ? 'webm' : 'm4a' }
                    : null;
                replaceAudio(next);
                recorderRef.current = null;
                setIsRecording(false);
                stopWaitersRef.current.splice(0).forEach(resolve => resolve(next));
            };
            recorderRef.current = recorder;
            replaceAudio(null);
            setSeconds(0);
            recorder.start();
            setIsRecording(true);
        } catch {
            stream.getTracks().forEach(track => track.stop());
            setError('failed');
        }
    };

    /** Arrête l'enregistrement en cours et rend le message (ou le dernier enregistré). */
    const stop = (): Promise<RecordedAudio | null> => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state !== 'recording') return Promise.resolve(audioRef.current);
        return new Promise(resolve => {
            stopWaitersRef.current.push(resolve);
            recorder.stop();
        });
    };

    const toggle = () => {
        if (recorderRef.current?.state === 'recording') void stop();
        else void start();
    };

    const clear = () => {
        if (recorderRef.current?.state === 'recording') {
            discardRef.current = true;
            recorderRef.current.stop();
        }
        replaceAudio(null);
        setSeconds(0);
        setError(null);
    };

    return { isRecording, seconds, audio, error, toggle, stop, clear, isSupported: isVoiceRecordingSupported() };
}
